// Reusable taxonomy filter chips for /music and /sermons.
// Renders compact controls that drive the `/api/tracks` filter params:
//   - main genre (single-select pill row, scrollable on mobile)
//   - mood tag (chip toggles, multi-select AND)
//   - energy slider (min..max)
//   - featured / recent toggles
//
// Keeps mobile clean: chips wrap and scroll horizontally; sliders collapse into
// a compact "Advanced" disclosure the user can open when they want.

import React, { useEffect, useMemo, useState } from "react";
import { Filter, ChevronDown, ChevronUp, Sparkles, Clock, Flame, X } from "lucide-react";
import { MAIN_GENRES, MOOD_TAG_SUGGESTIONS } from "../lib/taxonomy";
import { api } from "../lib/api";

const ENERGY_MIN = 1;
const ENERGY_MAX = 5;

export default function TaxonomyFilterBar({ value, onChange, testIdPrefix = "tax-filter" }) {
  const [open, setOpen] = useState(false);
  const [popularMood, setPopularMood] = useState([]);

  useEffect(() => {
    api.get("/taxonomy/popular-tags?kind=mood")
      .then((r) => setPopularMood(r.data?.tags || []))
      .catch(() => {});
  }, []);

  const moodOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const t of [...MOOD_TAG_SUGGESTIONS, ...popularMood.map((p) => p.display)]) {
      const k = (t || "").toLowerCase();
      if (!t || seen.has(k)) continue;
      seen.add(k);
      out.push(t);
    }
    return out;
  }, [popularMood]);

  const v = value || {};
  const mainGenre = v.main_genre || "";
  const moods = Array.isArray(v.mood_tags) ? v.mood_tags : [];
  const energyMin = Number.isFinite(v.energy_min) ? v.energy_min : null;
  const energyMax = Number.isFinite(v.energy_max) ? v.energy_max : null;
  const featured = !!v.featured;
  const recentDays = v.recent_days || null;

  const update = (partial) => onChange?.({ ...v, ...partial });
  const toggleMood = (m) => {
    const has = moods.some((x) => x.toLowerCase() === m.toLowerCase());
    update({ mood_tags: has ? moods.filter((x) => x.toLowerCase() !== m.toLowerCase()) : [...moods, m] });
  };

  const activeCount =
    (mainGenre ? 1 : 0) +
    moods.length +
    (energyMin || energyMax ? 1 : 0) +
    (featured ? 1 : 0) +
    (recentDays ? 1 : 0);

  const clearAll = () => onChange?.({});

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-root`}>
      {/* Main-genre row — horizontal scroll on mobile */}
      <div className="flex items-center gap-2 overflow-x-auto py-1 -mx-1 px-1" data-testid={`${testIdPrefix}-main-genre-row`}>
        <button
          type="button"
          onClick={() => update({ main_genre: "" })}
          className={`overline px-3 py-1.5 rounded-full border whitespace-nowrap transition-colors ${
            !mainGenre ? "bg-[#C5A059] text-[#060709] border-[#C5A059]" : "border-[#22262E] text-[#A1A4AB] hover:border-[#C5A059]/40 hover:text-[#FDFBF7]"
          }`}
          data-testid={`${testIdPrefix}-genre-all`}
        >
          All genres
        </button>
        {MAIN_GENRES.map((g) => (
          <button
            key={g}
            type="button"
            onClick={() => update({ main_genre: mainGenre === g ? "" : g })}
            className={`overline px-3 py-1.5 rounded-full border whitespace-nowrap transition-colors ${
              mainGenre === g ? "bg-[#C5A059] text-[#060709] border-[#C5A059]" : "border-[#22262E] text-[#A1A4AB] hover:border-[#C5A059]/40 hover:text-[#FDFBF7]"
            }`}
            data-testid={`${testIdPrefix}-genre-${g.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            {g}
          </button>
        ))}
      </div>

      {/* Disclosure: advanced filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setOpen((x) => !x)}
          className="overline text-xs px-3 py-1.5 rounded-full border border-[#22262E] text-[#A1A4AB] hover:text-[#FDFBF7] hover:border-[#C5A059]/40 inline-flex items-center gap-1.5"
          data-testid={`${testIdPrefix}-toggle-advanced`}
        >
          <Filter className="w-3.5 h-3.5" strokeWidth={1.5} />
          Filters {activeCount > 0 ? `· ${activeCount}` : ""}
          {open ? <ChevronUp className="w-3 h-3" strokeWidth={2} /> : <ChevronDown className="w-3 h-3" strokeWidth={2} />}
        </button>
        <button
          type="button"
          onClick={() => update({ featured: !featured })}
          className={`overline text-xs px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 transition-colors ${
            featured ? "bg-[#C5A059] text-[#060709] border-[#C5A059]" : "border-[#22262E] text-[#A1A4AB] hover:border-[#C5A059]/40 hover:text-[#FDFBF7]"
          }`}
          data-testid={`${testIdPrefix}-featured`}
        >
          <Sparkles className="w-3 h-3" strokeWidth={2} /> Featured
        </button>
        <button
          type="button"
          onClick={() => update({ recent_days: recentDays === 14 ? null : 14 })}
          className={`overline text-xs px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 transition-colors ${
            recentDays ? "bg-[#C5A059] text-[#060709] border-[#C5A059]" : "border-[#22262E] text-[#A1A4AB] hover:border-[#C5A059]/40 hover:text-[#FDFBF7]"
          }`}
          data-testid={`${testIdPrefix}-recent`}
        >
          <Clock className="w-3 h-3" strokeWidth={2} /> Recent
        </button>
        {activeCount > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="text-xs text-[#A1A4AB] hover:text-[#FDFBF7] inline-flex items-center gap-1"
            data-testid={`${testIdPrefix}-clear`}
          >
            <X className="w-3 h-3" strokeWidth={2} /> Clear all
          </button>
        )}
      </div>

      {open && (
        <div className="card-surface p-3 md:p-4 space-y-3" data-testid={`${testIdPrefix}-advanced-panel`}>
          <div>
            <div className="overline text-[10px] text-[#A1A4AB] mb-1.5">Mood</div>
            <div className="flex flex-wrap gap-1.5">
              {moodOptions.map((m) => {
                const active = moods.some((x) => x.toLowerCase() === m.toLowerCase());
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => toggleMood(m)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                      active ? "bg-[#C5A059]/15 border-[#C5A059] text-[#C5A059]" : "border-[#22262E] text-[#A1A4AB] hover:border-[#C5A059]/40 hover:text-[#FDFBF7]"
                    }`}
                    data-testid={`${testIdPrefix}-mood-${m.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div className="overline text-[10px] text-[#A1A4AB] mb-1.5 inline-flex items-center gap-1">
              <Flame className="w-3 h-3" strokeWidth={1.5} /> Energy
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {[1, 2, 3, 4, 5].map((n) => {
                const active = energyMin && energyMax && energyMin <= n && n <= energyMax;
                const onClick = () => {
                  // Toggle a single-energy filter; clicking the same value clears it.
                  if (energyMin === n && energyMax === n) {
                    update({ energy_min: null, energy_max: null });
                  } else {
                    update({ energy_min: n, energy_max: n });
                  }
                };
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={onClick}
                    className={`overline text-[10px] w-8 h-8 rounded-full border transition-colors ${
                      active ? "bg-[#C5A059] text-[#060709] border-[#C5A059]" : "border-[#22262E] text-[#A1A4AB] hover:border-[#C5A059]/40 hover:text-[#FDFBF7]"
                    }`}
                    data-testid={`${testIdPrefix}-energy-${n}`}
                  >
                    {n}
                  </button>
                );
              })}
              <span className="text-[10px] text-[#6C707A]">Tap a value to filter to that energy level</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Build URLSearchParams-friendly query for /api/tracks */
export function filtersToParams(filters) {
  const out = {};
  if (!filters) return out;
  if (filters.main_genre) out.main_genre = filters.main_genre;
  if (Array.isArray(filters.style_tags) && filters.style_tags.length) out.style_tags = filters.style_tags.join(",");
  if (Array.isArray(filters.mood_tags) && filters.mood_tags.length) out.mood_tags = filters.mood_tags.join(",");
  if (Number.isFinite(filters.energy_min)) out.energy_min = filters.energy_min;
  if (Number.isFinite(filters.energy_max)) out.energy_max = filters.energy_max;
  if (filters.featured) out.featured = true;
  if (filters.recent_days) out.recent_days = filters.recent_days;
  return out;
}
